import type {
  Ajustes, AjustesPlataforma, Borrador, Capacidad, Caso, Config, Corrida,
  CredencialInfo, DescubrimientoMCP, FilaTenant, Inteligencia, RespuestaCapas, ResultadoPrueba,
  Implementacion, Precio, ProveedorModelo, ResumenCorrida, TrazaReal, TurnoConversacion, Usuario, Veredicto, VersionGuardada, Vigencia, VistaAlertas, VistaErrores,
  Adopcion, CapaCompartida, CapaPendiente, Facturacion, HeredaDe, MensajeDePlataforma, Sello, VersionCapa, VersionCatalogo,
} from '@/tipos'
import type { FiltroAuditoria, RespuestaAuditoria } from '@/tipos/auditoria'
import type { AltaUsuarioDelPortal, ComercioDeCliente, NuevoComercio, UsuarioDelPortal } from '@/tipos/comercios'
import type { RolDelBot } from '@/tipos/roles'
import type { Diff } from '@/tipos'
import type {
  EnlaceEmbebido, FilaConversacionGestion, FilaGestion, PaginaDetalle, ResumenGestion,
} from '@/tipos/gestion'
import type { ResumenCampanas } from '@/tipos/campanas'
import type { MensajeSimulado, VistaSimulador } from '@/tipos/simulador'

// Cliente HTTP del API de administracion.
//
// Sin axios: fetch alcanza para nueve endpoints, y una dependencia menos es una
// dependencia menos que parchear. Lo unico que hace falta encapsular es el token
// y la forma de los errores.

export class ErrorAPI extends Error {
  /** `datos` es el cuerpo entero de la respuesta, no solo su `error`.
   *
   *  Varias rutas contestan un 409 con la INFORMACIÓN que hace falta para
   *  resolverlo —los pasos que no validan, las claves que se perderían— y
   *  quedarse solo con el mensaje obliga a ir a buscarlo a la base, que es
   *  justo lo que ese cuerpo existe para evitar. */
  constructor(public codigo: number, mensaje: string, public datos?: any) {
    super(mensaje)
  }
  get esSesionCaida() { return this.codigo === 401 }
  get esPermiso()     { return this.codigo === 403 }
  get esConflicto()   { return this.codigo === 409 }
}

const CLAVE_TOKEN = 'botia.token'

export const token = {
  leer:     () => localStorage.getItem(CLAVE_TOKEN) ?? '',
  guardar:  (t: string) => localStorage.setItem(CLAVE_TOKEN, t),
  olvidar:  () => localStorage.removeItem(CLAVE_TOKEN),
}

/** `crudo` manda el cuerpo tal cual, sin pasarlo por JSON. Es para el guion
 *  pegado: convertirlo a JSON aquí obligaría a interpretar el YAML en el
 *  navegador, y quien tiene que decidir si es válido es el servidor. */
async function pedir<T>(metodo: string, ruta: string, cuerpo?: unknown, crudo = false): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  const t = token.leer()
  if (t) cabeceras.Authorization = `Bearer ${t}`
  if (cuerpo !== undefined) cabeceras['Content-Type'] = crudo ? 'text/yaml' : 'application/json'

  let r: Response
  try {
    r = await fetch(ruta, {
      method: metodo,
      headers: cabeceras,
      body: cuerpo === undefined ? undefined : crudo ? String(cuerpo) : JSON.stringify(cuerpo),
    })
  } catch {
    // Distinguir "no hay red" de "el servidor dijo que no" importa: son dos
    // cosas distintas que hacer.
    throw new ErrorAPI(0, 'No se pudo contactar el servidor. ¿Está corriendo la consola?')
  }

  if (r.status === 204) return undefined as T

  const texto = await r.text()
  let datos: any = null
  try { datos = texto ? JSON.parse(texto) : null } catch { /* respuesta no JSON */ }

  if (!r.ok) {
    throw new ErrorAPI(r.status, datos?.error ?? `El servidor respondió ${r.status}`, datos)
  }
  return datos as T
}


/** Baja una ruta protegida como archivo.
 *
 * Va por fetch y no por un <a href>: todas estas rutas exigen el token en la
 * cabecera, y un enlace normal no lo lleva.
 *
 * El nombre lo pone el SERVIDOR en Content-Disposition —sabe de qué es y de qué
 * versión— y el de aquí es solo la red por si no viene. */
async function bajarArchivo(ruta: string, porDefecto: string) {
  const r = await fetch(ruta, { headers: { Authorization: `Bearer ${token.leer()}` } })
  if (!r.ok) {
    let msg = `El servidor respondió ${r.status}`
    try { msg = (JSON.parse(await r.text()))?.error ?? msg } catch { /* no era JSON */ }
    throw new ErrorAPI(r.status, msg)
  }
  const cd = r.headers.get('Content-Disposition') ?? ''
  const nombre = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? porDefecto
  return { blob: await r.blob(), nombre }
}

export const api = {
  // Entrar: los dos únicos que no llevan token, porque son por donde se entra.
  //
  // pedirCodigo responde igual exista o no la cuenta. Ese mensaje se muestra
  // tal cual y no se interpreta: distinguir aquí "no existe" de "ya va" haría
  // del formulario un directorio del personal.
  pedirCodigo: (correo: string) =>
    pedir<{ enviado: boolean; mensaje: string }>('POST', '/api/acceso/codigo', { correo }),

  entrarConCodigo: (correo: string, codigo: string) =>
    pedir<{ token: string }>('POST', '/api/acceso/entrar', { correo, codigo }),

  // Salir revoca la sesión en el SERVIDOR. Borrarla solo del navegador deja el
  // token vivo para quien lo tenga copiado.
  salir: () => pedir<{ cerrada: boolean }>('POST', '/api/acceso/salir', {}),

  yo: () => pedir<Usuario>('GET', '/api/yo'),

  // Quien puede entrar y desde donde. No da ni quita permisos: eso sigue en la
  // pantalla de seguridad del sistema viejo.
  usuariosConsola: () =>
    pedir<{ usuarios: Array<{
      id: number; username: string; nombre: string; email: string; celular: string
      permisos: string[]; sesiones: number; ultimo_acceso: string; email_duplicado: boolean
      /** De qué clientes responde, o la fila comodín "ALL". Solo significa
       *  algo para quien lleva `botia-leader`; para los demás llega vacía. */
      alcance: string[]
      /** Los roles DEL BOT que tiene activos, por id. Sus otros roles —los del
       *  sistema viejo— no se leen desde aquí. */
      roles_bot: number[]
    }> }>('GET', '/api/acceso/usuarios'),

  // Los roles del bot. Todo con botia-adm.
  //
  // Solo existen para la consola los roles que conceden EXCLUSIVAMENTE permisos
  // botia-*: repartir uno que dé menús del sistema viejo desde una pantalla que
  // solo enseña el bot sería dar más de lo que se ve.
  rolesDelBot: () =>
    pedir<{
      roles: RolDelBot[]; permisos: string[]
      /** Los roles que crea «Crear los roles básicos», uno por permiso. Vienen
       *  del servidor para que la confirmación enseñe los nombres que de verdad
       *  se van a crear. */
      basicos: Array<{ nombre: string; permiso: string }>
    }>('GET', '/api/acceso/roles-bot'),

  crearRolDelBot: (nombre: string, permisos: string[]) =>
    pedir<{ id: number }>('POST', '/api/acceso/roles-bot', { nombre, permisos }),

  // La lista ENTERA de permisos: cambiarlos mueve a todos los que llevan el rol.
  fijarPermisosDelRol: (id: number, permisos: string[]) =>
    pedir<{ guardado: boolean }>('PUT', `/api/acceso/roles-bot/${id}/permisos`, { permisos }),

  // Crea los roles básicos que falten, todos o ninguno. Pulsarlo dos veces no
  // duplica: devuelve cuáles creó y cuáles ya existían.
  crearRolesBasicosDelBot: () =>
    pedir<{ creados: string[]; ya_existian: string[] }>('POST', '/api/acceso/roles-bot/basicos'),

  // Busca en TODO el directorio, tenga o no permisos: sin esto no habría forma
  // de darle su primer rol a nadie, porque la lista de arriba solo enseña a
  // quien ya tiene alguno.
  buscarUsuarios: (texto: string) =>
    pedir<{ usuarios: Array<{
      id: number; username: string; nombre: string; email: string; roles_bot: number[]
    }> }>('GET', `/api/acceso/buscar-usuarios?q=${encodeURIComponent(texto)}`),

  // La lista ENTERA de roles del bot de esa persona. Los que no son del bot no
  // se tocan.
  fijarRolesDelBot: (id: number, roles: number[]) =>
    pedir<{ guardado: boolean }>('PUT', `/api/acceso/usuarios/${id}/roles`, { roles }),

  // Exige el permiso de publicar: el correo es por donde llega el codigo, asi
  // que cambiarselo a alguien es quedarse con su cuenta.
  guardarContacto: (id: number, email: string, celular: string) =>
    pedir<{ guardado: boolean; cambio_correo: boolean }>(
      'PUT', `/api/acceso/usuarios/${id}/contacto`, { email, celular }),

  // Exige botia-adm, no botia-pub como la de contacto: un líder lleva botia-pub
  // dentro de su alcance, y con eso podría ampliarse a sí mismo.
  //
  // Se manda la lista ENTERA, no un delta: con un delta la pantalla tendría que
  // saber qué había antes, y dos pestañas abiertas darían un alcance que nadie
  // pidió.
  guardarAlcance: (id: number, alcance: string[]) =>
    pedir<{ alcance: string[] }>(
      'PUT', `/api/acceso/usuarios/${id}/alcance`, { alcance }),

  sesionesConsola: () =>
    pedir<{ sesiones: Array<{
      id: string; usuario: string; nombre: string; creada: string
      ultimo_uso: string; expira: string; ip: string; agente: string
    }> }>('GET', '/api/acceso/sesiones'),

  revocarSesionConsola: (id: string) =>
    pedir<{ revocada: boolean }>('DELETE', `/api/acceso/sesiones/${encodeURIComponent(id)}`),

  /** Baja el guion como archivo. Sin versión, el PUBLICADO — que es lo que la
   *  gente quiere decir con "el guion actual": el que atiende ahora mismo.
   *
   *  Va por fetch y no por un <a href>: la ruta exige el token en la cabecera, y
   *  un enlace normal no lo lleva. */
  descargarYAML: (id: string, version?: number) =>
    bajarArchivo(
      `/api/tenants/${encodeURIComponent(id)}/yaml${version ? `?version=${version}` : ''}`,
      `${id}.yaml`),

  /** Clona el guion de otro tenant AL BORRADOR de este. Nunca publica: un guion
   *  clonado apunta al sistema del cliente de origen y le habla a la gente con
   *  el nombre de otra empresa. */
  clonarGuion: (destino: string, origen: string, version = 0) =>
    pedir<{
      clonado: boolean; origen: string; version_origen: number; nombre_origen: string
      base_version: number
      avisos: Array<{ campo: string; valor: string; detalle: string }>
      problemas: string[]
    }>('POST', `/api/tenants/${encodeURIComponent(destino)}/clonar`, { origen, version }),

  /** Quién espera a un asesor y desde cuándo. Sin esto, encender la caducidad
   *  de esperas es operar a ciegas: el único rastro está en el log del gateway. */
  cola: (id: string) =>
    pedir<{
      esperando: Array<{ motivo: string; cuantas: number; mas_vieja_min: number }>
      caducadas_24h: number
    }>('GET', `/api/tenants/${encodeURIComponent(id)}/cola`),

  catalogo: () => pedir<{ capacidades: Capacidad[] }>('GET', '/api/catalogo'),

  // ---------------------------------------------------------------------
  // El catálogo, versionado en la base
  // ---------------------------------------------------------------------
  // Leer lo puede quien edita; guardar y publicar exigen botia-adm. La línea
  // que lo separa de botia-pub es el RADIO: publicar un catálogo mueve lo que
  // heredarán todos, no un cliente.

  catalogoVersiones: () =>
    pedir<{ versiones: VersionCatalogo[]; cargada: number }>('GET', '/api/catalogo/versiones'),

  catalogoVersion: (v: number) =>
    pedir<{ version: number; capacidades: Capacidad[] }>('GET', `/api/catalogo/versiones/${v}`),

  /** Dos versiones del catálogo, línea a línea. Lo calcula el servidor. */
  diffCatalogo: (a: number, b: number) =>
    pedir<{ a: number; b: number; diff: Diff }>('GET', `/api/catalogo/versiones/${a}/diff/${b}`),

  /** Crea una versión. NO la publica. Los avisos vienen siempre, se guarde o
   *  no: son lo único que dice qué va a pasar cuando alguien adopte. */
  guardarCatalogo: (yaml: string, nota = '') =>
    pedir<{ version: number; nueva: boolean; avisos: string[] }>(
      'POST', '/api/catalogo/versiones', { yaml, nota }),

  /** Baja el catálogo como archivo. Sin versión, el VIGENTE — el que están
   *  heredando los clientes ahora mismo.
   *
   *  Existe porque editarlo era pegar 3.000 líneas sin tener de dónde partir:
   *  había que ir a buscarlas al repositorio y confiar en que fueran las
   *  mismas que están publicadas. Editar una copia vieja revierte en silencio
   *  lo que otro publicó mientras tanto. */
  descargarCatalogo: (version?: number) =>
    bajarArchivo(`/api/catalogo/yaml${version ? `?version=${version}` : ''}`, `capacidades.yaml`),

  /** Dice si un YAML sirve SIN crear versión.
   *
   *  Responde 200 con `ok:false` cuando el texto está mal: el error no es de la
   *  petición, es del contenido por el que se preguntaba. */
  comprobarCatalogo: (yaml: string) =>
    pedir<{ ok: boolean; error?: string; capacidades?: number }>(
      'POST', '/api/catalogo/comprobar', { yaml }),

  publicarCatalogo: (v: number, motivo = '') =>
    pedir<{ version: number; aviso: string }>(
      'POST', `/api/catalogo/versiones/${v}/publicar`, { motivo }),

  /** A quién rompe obsoletar algo. Es lo que hay que ver ANTES de publicar. */
  impactoCatalogo: (v: number) =>
    pedir<{ version: number; obsoletas: Array<{ capacidad: string; tenants: HeredaDe[] }> }>(
      'GET', `/api/catalogo/versiones/${v}/impacto`),

  // ---------------------------------------------------------------------
  // Las capas COMPARTIDAS
  // ---------------------------------------------------------------------
  // Ojo con la hermana: `capas(id)` es la capa PROPIA de un cliente. Estas las
  // heredan varios, y por eso publicarlas exige botia-adm.

  capasCompartidas: () =>
    pedir<{ capas: CapaCompartida[]; sin_publicar: CapaPendiente[] }>('GET', '/api/capas'),

  versionesDeCapa: (nombre: string) =>
    pedir<{ nombre: string; versiones: VersionCapa[] }>(
      'GET', `/api/capas/${encodeURIComponent(nombre)}/versiones`),

  yamlDeCapa: (nombre: string, v: number) =>
    pedir<{ nombre: string; version: number; yaml: string }>(
      'GET', `/api/capas/${encodeURIComponent(nombre)}/versiones/${v}`),

  /** Dos versiones de una capa, línea a línea. */
  diffCapa: (nombre: string, a: number, b: number) =>
    pedir<{ a: number; b: number; diff: Diff }>(
      'GET', `/api/capas/${encodeURIComponent(nombre)}/versiones/${a}/diff/${b}`),

  guardarCapa: (nombre: string, yaml: string, nota = '') =>
    pedir<{ nombre: string; version: number; nueva: boolean }>(
      'POST', `/api/capas/${encodeURIComponent(nombre)}/versiones`, { yaml, nota }),

  /** Baja una capa compartida. Sin versión, la VIGENTE. */
  descargarCapa: (nombre: string, version?: number) =>
    bajarArchivo(
      `/api/capas/${encodeURIComponent(nombre)}/yaml${version ? `?version=${version}` : ''}`,
      `${nombre}.yaml`),

  comprobarCapa: (nombre: string, yaml: string) =>
    pedir<{ ok: boolean; error?: string; claves?: number }>(
      'POST', `/api/capas/${encodeURIComponent(nombre)}/comprobar`, { yaml }),

  publicarCapa: (nombre: string, v: number, motivo = '') =>
    pedir<{ nombre: string; version: number; aviso: string; heredan: HeredaDe[] }>(
      'POST', `/api/capas/${encodeURIComponent(nombre)}/versiones/${v}/publicar`, { motivo }),

  /** Quién hereda de esta capa y en qué versión está sellado cada uno. Es el
   *  radio de daño, y se enseña antes de publicar. */
  impactoCapa: (nombre: string) =>
    pedir<{ nombre: string; heredan: HeredaDe[] }>(
      'GET', `/api/capas/${encodeURIComponent(nombre)}/impacto`),

  // ---------------------------------------------------------------------
  // Adoptar
  // ---------------------------------------------------------------------

  /** Recompone el guion del cliente contra otras versiones y lo guarda. NO
   *  publica: la versión queda con las evaluaciones en pendiente. */
  adoptar: (id: string, catalogo?: number, bases?: Record<string, number>, nota = '') =>
    pedir<Adopcion>('POST', `/api/tenants/${encodeURIComponent(id)}/adoptar`,
      { catalogo: catalogo ?? 0, bases: bases ?? {}, nota }),

  /** Adopta lo vigente, lo publica y lo comprueba releyendo la base. Un paso
   *  donde antes había tres, y funciona también sin sello: adopta contra las
   *  capas VIGENTES en vez de comparar contra lo sellado. */
  alDia: (id: string, nota = '', conservar: string[] = []) =>
    pedir<Adopcion & { publicada: boolean }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/al-dia`, { nota, conservar }),

  /** De qué está hecha la versión vigente de un cliente. */
  sello: (id: string) =>
    pedir<{ tenant: string; sello: Sello | null; texto: string }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/sello`),

  tenants: () => pedir<{ tenants: FilaTenant[] }>('GET', '/api/tenants'),

  /** Da de alta un cliente subiendo su `.fuente.yaml`. Queda su v1 SIN
   *  publicar. Exige botia-adm. Un 409 es «ya vive en la base»; un 400 con
   *  `problemas` es que el guion no valida y no se escribió nada. */
  altaDesdeArchivo: (yaml: string) =>
    pedir<{ tenant: string; nombre: string; claves: number; version: number; sello: string; avisos: string[] }>(
      'POST', '/api/tenants', yaml, true),

  vigente: (id: string) => pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}`),

  borrador: (id: string) => pedir<Borrador>('GET', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  guardarBorrador: (id: string, config: Config, base_version: number, nota = '') =>
    pedir<{ guardado: boolean; problemas: string[] }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/borrador`, { config, base_version, nota }),

  /** Guarda como borrador un guion pegado tal cual. Es la puerta para las
   *  partes del config que no tienen formulario propio —el mapeo del adapter,
   *  por ejemplo—, que si no obligan a salir de la consola. */
  pegarYAML: (id: string, yaml: string) =>
    pedir<{ guardado: boolean; aviso?: string; problemas: string[] }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/borrador/yaml`, yaml, true),

  descartarBorrador: (id: string) =>
    pedir<{ descartado: boolean }>('DELETE', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  /** De dónde sale cada texto del guion: de este cliente o de lo que comparte
   *  con los demás de su vertical.
   *
   *  `bajo` pide solo una rama — 'plantillas' para la vista de mensajes. Sin
   *  ella vienen las claves del guion entero, que para una pantalla que solo
   *  enseña mensajes es traerse cincuenta y seis pasos para nada. */
  capas: (id: string, bajo?: string) =>
    pedir<RespuestaCapas>('GET',
      `/api/tenants/${encodeURIComponent(id)}/capas${bajo ? `?bajo=${encodeURIComponent(bajo)}` : ''}`),

  /** Fija una clave para este cliente. La clave va en el cuerpo y no en la
   *  ruta: lleva puntos y escapes, y meterla en una URL obliga a escaparla dos
   *  veces con dos reglas distintas. */
  /** `crudo` manda el valor como fragmento YAML ya escrito, en vez de dejar
   *  que el servidor lo codifique como texto.
   *
   *  Sin él sólo se pueden fijar TEXTOS: el servidor pasa todo por
   *  `FragmentoDeTexto`, y eso es correcto para un mensaje —«Atención: llame
   *  ya» metido crudo se leería como un mapa— pero imposibilita fijar una
   *  lista, un número o una rama entera. El campo existía en la API desde el
   *  principio; lo que faltaba era que alguien lo mandara. */
  fijarClave: (id: string, clave: string, valor: string, crudo = false) =>
    pedir<{ clave: string; capa: string }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/capas`, { clave, valor, crudo }),

  /** Quita la clave: ese texto vuelve a ser el compartido, y vuelve a seguir a
   *  la vertical cuando la vertical cambie. */
  heredarClave: (id: string, clave: string) =>
    pedir<{ clave: string }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/capas/heredar`, { clave }),

  /** Los mensajes que dice el motor, con su texto de plataforma.
   *
   *  Sin tenant: la lista es la misma para todos, y lo que cada cliente haya
   *  escrito encima llega por `capas(id, 'plantillas')` como cualquier otro
   *  mensaje suyo. */
  mensajesDePlataforma: () =>
    pedir<{ mensajes: MensajeDePlataforma[] }>('GET', '/api/mensajes-plataforma'),

  validar: (id: string, config: Config) =>
    pedir<{ problemas: string[] }>('POST', `/api/tenants/${encodeURIComponent(id)}/validar`, { config }),

  probar: (id: string, config: Config, mensajes: string[], escrituras = false) =>
    pedir<ResultadoPrueba>('POST', `/api/tenants/${encodeURIComponent(id)}/probar`,
      { config, mensajes, escrituras }),

  versiones: (id: string) =>
    pedir<{ versiones: VersionGuardada[]; historial: Vigencia[] }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/versiones`),

  version: (id: string, v: number) =>
    pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}/versiones/${v}`),

  /** Dos versiones del guion, línea a línea. */
  diffVersiones: (id: string, a: number, b: number) =>
    pedir<{ a: number; b: number; diff: Diff }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/versiones/${a}/diff/${b}`),

  crearVersion: (id: string, nota: string) =>
    pedir<{ version: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/versiones`, { nota }),

  publicar: (id: string, version: number, motivo: string, forzar = false) =>
    pedir<{ publicada: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/publicar`,
      { version, motivo, forzar }),

  despublicar: (id: string, motivo: string) =>
    pedir<{ despublicado: boolean }>('POST', `/api/tenants/${encodeURIComponent(id)}/despublicar`, { motivo }),

  secretos: (id: string) =>
    pedir<{ secretos: Array<{ Nombre: string; Cifrado: boolean; Autor: string; Nota: string; Actualizado: string }> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/secretos`),

  // Devuelve el valor DESCIFRADO de un secreto. Exige botia-adm y queda
  // registrado en el log quién lo miró: ver secretoRevelar en el servidor.
  //
  // De uno en uno a propósito — no hay «dame todos los de este cliente».
  verSecreto: (id: string, nombre: string) =>
    pedir<{ nombre: string; valor: string }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/secretos/${encodeURIComponent(nombre)}`),

  guardarSecreto: (id: string, nombre: string, valor: string, nota = '') =>
    pedir<{ guardado: boolean; caracteres: number; cifrado: boolean; recortado: number }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/secretos/${encodeURIComponent(nombre)}`, { valor, nota }),

  // desde/hasta en YYYY-MM-DD, estado en {con_asesor, cerrada, en_curso, en_limbo}.
  // Vacíos traen todo, que es lo que ve quien entra sin filtrar nada.
  sesiones: (id: string, desde = '', hasta = '', estado = '') => {
    const q = new URLSearchParams()
    if (desde) q.set('desde', desde)
    if (hasta) q.set('hasta', hasta)
    if (estado) q.set('estado', estado)
    const cola = q.toString() ? `?${q}` : ''
    return pedir<{
      sesiones: Array<{
        conversacion: string; paso: string; turnos: number; actualizada: string
        estado: string; inactiva_min: number
      }>
      resumen: Record<string, number>
    }>('GET', `/api/tenants/${encodeURIComponent(id)}/sesiones${cola}`)
  },

  reiniciarSesion: (id: string, conversacion: string) =>
    pedir<{ reiniciada: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/sesiones/${encodeURIComponent(conversacion)}`),

  /** El ida y vuelta con una persona, ordenado en el tiempo. La cola de salida
   *  es media conversación: se ve lo que contestó el bot y hay que adivinar a
   *  qué. */
  conversacion: (id: string, con: string, limite = 100) => {
    const q = new URLSearchParams({ con, limite: String(limite) })
    return pedir<{ turnos: TurnoConversacion[]; trazas?: TrazaReal[]; conversacion_id?: string }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/conversacion?${q}`)
  },

  /** La misma conversación, pero pedida por su ID en vez de por teléfono: es lo
   *  que abre una fila del reporte de gestión, que ya trae el ID y no el
   *  número. */
  conversacionPorId: (id: string, conv: string) =>
    pedir<{ turnos: TurnoConversacion[]; conversacion_id?: string }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/conversacion?conv=${encodeURIComponent(conv)}&limite=200`),

  gestion: (id: string, mes: string) =>
    pedir<ResumenGestion>('GET', `/api/tenants/${encodeURIComponent(id)}/gestion?mes=${encodeURIComponent(mes)}`),
  gestionGestiones: (id: string, q: Record<string, string>) =>
    pedir<PaginaDetalle<FilaGestion>>('GET', `/api/tenants/${encodeURIComponent(id)}/gestion/gestiones?${new URLSearchParams(q)}`),
  gestionConversaciones: (id: string, q: Record<string, string>) =>
    pedir<PaginaDetalle<FilaConversacionGestion>>('GET', `/api/tenants/${encodeURIComponent(id)}/gestion/conversaciones?${new URLSearchParams(q)}`),

  /** Qué rindió cada envío de cobranza, por campaña y en total. `desde`/`hasta`
   *  son AAAA-MM-DD: el servidor (`gestion.RangoDe`) los interpreta en Bogotá y
   *  rechaza con 400 un rango al revés o mal escrito. */
  campanas: (id: string, desde: string, hasta: string) =>
    pedir<ResumenCampanas>('GET',
      `/api/tenants/${encodeURIComponent(id)}/campanas?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`),
  /** El mismo reporte, como CSV. Va por `bajarArchivo` y no por `pedir`: esta
   *  ruta no devuelve JSON cuando `formato=csv`. */
  descargarCampanasCSV: (id: string, desde: string, hasta: string) =>
    bajarArchivo(
      `/api/tenants/${encodeURIComponent(id)}/campanas?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}&formato=csv`,
      `campanas-${desde}-a-${hasta}.csv`),

  // Los comercios de un cliente y los usuarios de su portal. Ver
  // internal/consola/comercios.go: el alta de aquí es la ÚNICA forma de darle el
  // primer usuario a un portal, porque el dueño todavía no puede entrar.
  comercios: (id: string) =>
    pedir<{ comercios: ComercioDeCliente[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/comercios`),
  /** Da de alta un comercio. 201 trae el código ya normalizado; un 409 trae además
   *  `codigo` (codigo_en_uso, whatsapp_aviso_en_uso, correo_aviso_en_uso) en
   *  `ErrorAPI.datos`, para marcar el campo que chocó. */
  altaComercio: (id: string, datos: NuevoComercio) =>
    pedir<{ id: number; codigo: string }>('POST',
      `/api/tenants/${encodeURIComponent(id)}/comercios`, datos),
  usuariosDelPortal: (id: string, comercio: number) =>
    pedir<{ usuarios: UsuarioDelPortal[] }>('GET',
      `/api/tenants/${encodeURIComponent(id)}/comercios/${comercio}/usuarios`),
  altaUsuarioDelPortal: (id: string, comercio: number, datos: AltaUsuarioDelPortal) =>
    pedir<{ id: number }>('POST',
      `/api/tenants/${encodeURIComponent(id)}/comercios/${comercio}/usuarios`, datos),
  desactivarUsuarioDelPortal: (id: string, comercio: number, usuario: number) =>
    pedir<{ desactivado: boolean }>('POST',
      `/api/tenants/${encodeURIComponent(id)}/comercios/${comercio}/usuarios/${usuario}/desactivar`),
  // Simulador de WhatsApp. El número se manda tal cual lo escribió la persona y el
  // servidor DEVUELVE el canónico (57 + 10 dígitos): la pantalla usa siempre ese.
  simulador: (id: string, comercio?: number) =>
    pedir<VistaSimulador>('GET', `/api/tenants/${encodeURIComponent(id)}/simulador`
      + (comercio ? `?comercio=${encodeURIComponent(String(comercio))}` : '')),
  retenerNumero: (id: string, cuerpo: { numero: string } | { comercio_id: number }) =>
    pedir<{ numero: string }>('POST', `/api/tenants/${encodeURIComponent(id)}/simulador/numeros`, cuerpo),
  soltarNumero: (id: string, numero: string) =>
    pedir<{ numero: string; soltado: boolean }>('DELETE',
      `/api/tenants/${encodeURIComponent(id)}/simulador/numeros/${encodeURIComponent(numero)}`),
  escribirSimulado: (id: string, numero: string, texto: string) =>
    pedir<{ encolado: boolean }>('POST', `/api/tenants/${encodeURIComponent(id)}/simulador/mensajes`, { numero, texto }),
  // Tocar un botón o una fila: lo mismo que manda GupShup en WhatsApp. La `clave` de un
  // botón es su postbackText; la de una fila, su postback (el id de la fila).
  tocarSimulado: (id: string, numero: string, respuesta: { tipo: 'boton' | 'fila'; titulo: string; clave: string }) =>
    pedir<{ encolado: boolean }>('POST', `/api/tenants/${encodeURIComponent(id)}/simulador/mensajes`, { numero, respuesta }),
  mensajesSimulados: (id: string, numero: string, desdeEntrada: number, desdeSalida: number) =>
    pedir<{ mensajes: MensajeSimulado[] }>('GET',
      `/api/tenants/${encodeURIComponent(id)}/simulador/mensajes?numero=${encodeURIComponent(numero)}`
      + `&desde_entrada=${desdeEntrada}&desde_salida=${desdeSalida}`),
  enlaces: (id: string) =>
    pedir<{ enlaces: EnlaceEmbebido[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/enlaces`),
  crearEnlace: (id: string, datos: { nombre: string; dominios: string[]; expira_at?: string }) =>
    pedir<{ enlace: EnlaceEmbebido; token: string }>('POST', `/api/tenants/${encodeURIComponent(id)}/enlaces`, datos),
  revocarEnlace: (id: string, enlace: number) =>
    pedir<{ revocado: boolean }>('DELETE', `/api/tenants/${encodeURIComponent(id)}/enlaces/${enlace}`),

  /** Qué está fallando en este cliente, ahora.
   *
   *  El tablero de Inteligencia dice «errores: 340» y la línea de tiempo de una
   *  conversación muestra la traza entera pero exige saber el teléfono de
   *  antemano. Entre las dos faltaba esto. */
  errores: (id: string, opciones: { horas?: number; clase?: string; capacidad?: string } = {}) => {
    const q = new URLSearchParams()
    if (opciones.horas) q.set('horas', String(opciones.horas))
    if (opciones.clase) q.set('clase', opciones.clase)
    if (opciones.capacidad) q.set('capacidad', opciones.capacidad)
    const cola = q.toString() ? `?${q}` : ''
    return pedir<VistaErrores>('GET', `/api/tenants/${encodeURIComponent(id)}/errores${cola}`)
  },

  salidas: (id: string, para = '', limite = 100) => {
    const q = new URLSearchParams({ limite: String(limite) })
    if (para) q.set('para', para)
    return pedir<{ salidas: Array<Record<string, unknown>> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/salidas?${q}`)
  },

  // -------------------------------------------------------------------------
  // Evaluaciones
  // -------------------------------------------------------------------------

  casos: (id: string) =>
    pedir<{ casos: Caso[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/casos`),

  guardarCaso: (id: string, caso: Partial<Caso>) =>
    pedir<{ id: string }>('POST', `/api/tenants/${encodeURIComponent(id)}/casos`, caso),

  borrarCaso: (id: string, caso: string) =>
    pedir<{ borrado: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/casos/${encodeURIComponent(caso)}`),

  // Propone un caso a partir de una conversación real. No lo guarda: quien la
  // convierte sabe por qué la está guardando, y esa intención es justo lo que
  // hay que escribir en los invariantes.
  casoDesdeConversacion: (id: string, conversacion: string, horas = 24) =>
    pedir<{ caso: Caso; aviso: string }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/casos/desde-conversacion`,
      { conversacion, horas }),

  evaluar: (id: string, config?: Config, casos: string[] = [], guardar = false) =>
    pedir<{ problemas: string[]; corrida: Corrida; avisos?: string[] }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/evaluar`, { config, casos, guardar }),

  corridas: (id: string, limite = 30) =>
    pedir<{ corridas: ResumenCorrida[] }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas?limite=${limite}`),

  corrida: (id: string, corrida: string) =>
    pedir<Corrida>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas/${encodeURIComponent(corrida)}`),

  // -------------------------------------------------------------------------
  // Inteligencia
  // -------------------------------------------------------------------------
  // Estos ajustes NO se versionan: aplican al guardar. Es deliberado — apagar
  // la IA de un cliente a las once de la noche no puede exigir publicar.

  inteligencia: (id: string) =>
    pedir<Inteligencia>('GET', `/api/tenants/${encodeURIComponent(id)}/inteligencia`),

  guardarInteligencia: (id: string, ajustes: Ajustes) =>
    pedir<Inteligencia>('PUT', `/api/tenants/${encodeURIComponent(id)}/inteligencia`, ajustes),

  // Los globales sí: mueven a los treinta clientes a la vez.
  plataforma: () => pedir<AjustesPlataforma>('GET', '/api/plataforma/ajustes'),

  guardarPlataforma: (ajustes: Ajustes, motivo: string, publicar = false) =>
    pedir<{ version: number; publicada: boolean }>(
      'PUT', '/api/plataforma/ajustes', { ajustes, motivo, publicar }),

  publicarPlataforma: (version: number, motivo: string) =>
    pedir<{ publicada: number }>('POST', '/api/plataforma/ajustes', { version, motivo }),

  // -------------------------------------------------------------------------
  // Alertas de fallas graves
  // -------------------------------------------------------------------------
  // La CONFIGURACIÓN no viaja por aquí: `alertas` es un campo de los ajustes, así
  // que se guarda con guardarPlataforma o guardarInteligencia, y hereda, valida
  // y se versiona como todo lo demás. Un segundo camino para escribir lo mismo
  // se desincronizaría a la primera.
  //
  // Lo que sí viene de aquí es lo que aquellos no pueden dar: qué clases de
  // falla sabe detectar el servidor, qué se avisó de verdad, y la prueba.

  alertas: (tenant?: string) =>
    pedir<VistaAlertas>('GET', tenant
      ? `/api/tenants/${encodeURIComponent(tenant)}/alertas`
      : '/api/plataforma/alertas'),

  /** Manda un correo de prueba a los destinos QUE SE LE PASAN, no a los
   *  guardados: se usa mientras se escribe la lista, antes de guardarla.
   *
   *  Sin esto nadie sabe si la configuración está bien hasta el primer
   *  incidente, que es el peor momento para descubrir un typo en una dirección. */
  probarAlertas: (destinos: string[]) =>
    pedir<{ enviados: number; fallos: Record<string, string> }>(
      'POST', '/api/plataforma/alertas', { destinos }),

  // -------------------------------------------------------------------------
  // Credenciales de los proveedores de modelo
  // -------------------------------------------------------------------------
  // No hay método para leer una clave, y no es un olvido: el API no la devuelve
  // por ningún camino. Se registra y se reemplaza.

  credenciales: (tenant?: string) =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean; proveedores: ProveedorModelo[] }>(
      'GET', rutaCredenciales(tenant)),

  // Todas las de todos los clientes. Responde "¿quién factura contra su propio
  // contrato y quién contra el nuestro?" sin abrir treinta pantallas.
  // Los precios se administran aquí, no en el .env: un proveedor los cambia sin
  // avisarnos, y ajustarlos no puede exigir un reinicio.
  // Los proveedores de modelo. Son datos: agregar uno que hable un protocolo ya
  // implementado —Groq, DeepSeek, Azure, un vLLM propio— es una fila y una
  // credencial con su base_url, sin desplegar nada.
  proveedores: () =>
    pedir<{ proveedores: ProveedorModelo[]; implementaciones: Implementacion[] }>(
      'GET', '/api/plataforma/proveedores'),

  guardarProveedor: (p: ProveedorModelo) =>
    pedir<{ guardado: boolean }>('PUT', '/api/plataforma/proveedores', p),

  borrarProveedor: (id: string) =>
    pedir<{ borrado: boolean }>('DELETE', `/api/plataforma/proveedores/${encodeURIComponent(id)}`),

  precios: () =>
    pedir<{ precios: Precio[]; ayuda: string }>('GET', '/api/plataforma/precios'),

  guardarPrecio: (p: Precio) =>
    pedir<{ guardado: boolean }>('PUT', '/api/plataforma/precios', p),

  borrarPrecio: (proveedor: string, modelo: string) =>
    pedir<{ borrado: boolean }>('DELETE',
      `/api/plataforma/precios/${encodeURIComponent(proveedor)}/${encodeURIComponent(modelo)}`),

  /** La factura de IA de TODOS los clientes. La de uno solo está en su pestaña
   *  de Inteligencia; esta es la que se cuadra contra el cobro del proveedor.
   *
   *  Vacíos = el mes en curso y sin desglosar por modelo, que es como llega la
   *  factura. `hasta` es inclusivo. */
  facturacion: (desde = '', hasta = '', proveedor = '', modelo = '') => {
    const q = new URLSearchParams()
    if (desde) q.set('desde', desde)
    if (hasta) q.set('hasta', hasta)
    if (proveedor) q.set('proveedor', proveedor)
    if (modelo) q.set('modelo', modelo)
    const cola = q.toString()
    return pedir<Facturacion>('GET', '/api/plataforma/facturacion' + (cola ? '?' + cola : ''))
  },

  /** Quién hizo qué. Exige botia-adm: es la misma lista que dice quién leyó
   *  qué token.
   *
   *  Vacíos = los últimos 30 días, sin filtrar. `hasta` es inclusivo. */
  auditoria: (f: FiltroAuditoria, pagina = 0) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v) q.set(k, v)
    if (pagina > 0) q.set('pagina', String(pagina))
    const cola = q.toString()
    return pedir<RespuestaAuditoria>('GET', '/api/auditoria' + (cola ? '?' + cola : ''))
  },

  // La puerta para pasar a modo agente. No prohíbe: informa.
  promocion: (id: string) =>
    pedir<{ veredicto: Veredicto; evidencia: Record<string, unknown> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/promocion`),

  // Lo que hace falta para conectar un agente, y qué vería si se conecta.
  mcp: (id: string) =>
    pedir<DescubrimientoMCP>('GET', `/api/tenants/${encodeURIComponent(id)}/mcp`),

  credencialesTodas: () =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean }>(
      'GET', '/api/plataforma/credenciales?todas=1'),

  guardarCredencial: (tenant: string | undefined, c: {
    proveedor: string; clave: string; base_url?: string; organizacion?: string; nota?: string
  }) =>
    pedir<{ guardada: boolean; cifrada: boolean; caracteres: number; recortados: number }>(
      'PUT', rutaCredenciales(tenant), c),

  activarCredencial: (tenant: string | undefined, proveedor: string, activa: boolean) =>
    pedir<{ activa: boolean }>(
      'POST', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`, { activa }),

  borrarCredencial: (tenant: string | undefined, proveedor: string) =>
    pedir<{ borrada: boolean }>(
      'DELETE', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`),
}

// Sin tenant son las de la plataforma: la cuenta contra la que factura todo el
// que no traiga la suya.
function rutaCredenciales(tenant?: string) {
  return tenant
    ? `/api/tenants/${encodeURIComponent(tenant)}/credenciales`
    : '/api/plataforma/credenciales'
}
